/**
 * Who may write to the start screen's status line and busy flag.
 *
 * A flow that runs for a while (a resume that is retrying on a bad link) shows "Finding your round…" and
 * disables the buttons, and takes both away when it is done. Meanwhile the player can start something else,
 * which writes a status of its own. When the slow flow finally finishes it must clear only what IT set: wiping a
 * newer flow's status, or freeing a button the newer flow has disabled, is the bug this exists to prevent.
 *
 * Ordinary writers (`status`, `busy`) take ownership of what they write: the status line and the busy flag are owned separately. A long-running flow `claim`s: its writes are
 * applied while nothing has written since, and ignored after, so its closing "clear" is harmless to a successor.
 */

export interface StartUiTarget {
  setStatus(text: string, tone?: 'neutral' | 'rose'): void;
  setBusy(on: boolean): void;
}

export interface OwnedStartUi {
  status(text: string, tone?: 'neutral' | 'rose'): void;
  busy(on: boolean): void;
}

export function createStartUi(target: StartUiTarget): OwnedStartUi & { claim(): OwnedStartUi } {
  // Two owners, because they are two things: a writer that sets the status line does not thereby own the buttons
  // (a start queued before boot writes a status and nothing else, and must not strand the busy flag the boot
  // resume set and is about to clear).
  let statusOwner = 0;
  let busyOwner = 0;
  return {
    status(text, tone) {
      statusOwner++;
      target.setStatus(text, tone);
    },
    busy(on) {
      busyOwner++;
      target.setBusy(on);
    },
    claim() {
      const mineStatus = ++statusOwner;
      const mineBusy = ++busyOwner;
      return {
        status(text, tone) {
          if (statusOwner === mineStatus) target.setStatus(text, tone);
        },
        busy(on) {
          if (busyOwner === mineBusy) target.setBusy(on);
        },
      };
    },
  };
}
